import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import {NgClass} from '@angular/common';
import {
  ArchitecturalPrinciple,
  CountryCode,
  DirectusService,
  DirectusTranslation,
  HrToolServiceModel,
  LanguageCode,
  Module,
  ModuleFunction,
} from '@frontend/directus';
import {CountryService} from '@frontend/shared';
import {AccordionModule} from 'primeng/accordion';
import {Button} from 'primeng/button';
import {CheckboxModule} from 'primeng/checkbox';
import {InputNumberModule} from 'primeng/inputnumber';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {CountryMultiselectComponent} from '../../../shared/components/inputs/country-multiselect/country-multiselect.component';
import {LanguageMultiselectComponent} from '../../../shared/components/inputs/language-multiselect/language-multiselect.component';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {HrToolService} from '../hr-tool.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {CountryListComponent} from '../../../shared/components/list/country-list/country-list.component';
import {Card} from 'primeng/card';
import {NavigationEnd, Router} from '@angular/router';
import {debounceTime, distinctUntilChanged, filter, Subscription} from 'rxjs';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {ModuleFunctionLabelComponent} from '../../../shared/components/module-function-label/module-function-label.component';
import {CardTitleComponent} from '../../../shared/components/header/card-title/card-title.component';
import {CertificationLabelComponent} from '../../../shared/components/certification-label/certification-label.component';
import {DirectusTooltipComponent} from '../../../shared/components/directus-tooltip/directus-tooltip.component';
import {HrToolAiCapabilityFilterComponent} from './hr-tool-ai-capability-filter/hr-tool-ai-capability-filter.component';
import {AiCapabilitySelectionChange} from './hr-tool-ai-capability-filter/hr-tool-ai-capability-filter.types';
import {HR_TOOL_AI_CAPABILITY_TYPES} from '../../../shared/utils/hr-tool-ai-capability.utils';

type ChipKind =
  | 'applicationType'
  | 'function'
  | 'serviceModel'
  | 'aiCapability'
  | 'certification'
  | 'country'
  | 'language'
  | 'companySize';

interface ActiveFilterChip {
  kind: ChipKind;
  id: string | number;
  label: string;
}

@Component({
  selector: 'app-hr-tool-filter',
  imports: [
    NgClass,
    AccordionModule,
    Button,
    CheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoPipe,
    LanguageMultiselectComponent,
    CountryMultiselectComponent,
    CountryListComponent,
    Card,
    InputNumberModule,
    TranslateDirectusPipe,
    ModuleFunctionLabelComponent,
    CardTitleComponent,
    CertificationLabelComponent,
    DirectusTooltipComponent,
    HrToolAiCapabilityFilterComponent,
  ],
  templateUrl: './hr-tool-filter.component.html',
  providers: [TranslateDirectusPipe],
})
export class HrToolFilterComponent implements OnInit, OnDestroy {
  private readonly directusService = inject(DirectusService);
  private readonly countryService = inject(CountryService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly hrToolsService = inject(HrToolService);
  private readonly translocoService = inject(TranslocoService);
  private readonly translateDirectusPipe = inject(TranslateDirectusPipe);
  private readonly router = inject(Router);

  private readonly subscriptions = new Subscription();

  protected moduleFunctionsMap = signal<Map<number, Module>>(new Map());
  protected availableModuleList: Module[] = [];

  /** Bumped whenever reference data (models, certs, functions, languages) finishes loading, so the
   * active-filter chip labels — resolved from those non-signal arrays — recompute once available. */
  private readonly referenceVersion = signal(0);

  protected availableServiceModels: HrToolServiceModel[] = [];
  protected availableLanguages = signal<LanguageCode[]>([]);

  protected readonly availableCertifications = toSignal(
    this.directusService.loadDirectusDropdown('hr_tool_certification'),
    {initialValue: []},
  );
  certificationForm: FormGroup = this.formBuilder.group({
    selectedCertifications: this.formBuilder.array<FormControl<boolean>>([]),
  });
  companySizeControl = new FormControl<number | null>(null);

  protected readonly aiCapabilityFacetTools = this.hrToolsService.aiCapabilityFacetTools;
  protected readonly selectedAiCapabilities = computed(
    () => this.hrToolsService.filters().aiCapabilities ?? [],
  );

  activeLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  private readonly allAvailableCountryCodes = computed(() =>
    this.countryService.allCountries().map((c) => c.isoCode),
  );
  readonly sortedAvailableCountries: Signal<CountryCode[]> =
    this.countryService.getFilteredAndSortedCountries(
      this.allAvailableCountryCodes,
      this.activeLanguage,
    );

  selectedFunctionIds = new Set<number>();
  serviceModelForm: FormGroup = this.formBuilder.group({
    selectedServiceModels: this.formBuilder.array<FormControl<boolean>>([]),
  });
  selectedCountries: CountryCode[] = [];
  selectedLanguages: LanguageCode[] = [];

  private readonly topLevelManualOpenPanels = signal<string[]>([]);
  private readonly topLevelManualOverride = signal<Set<string>>(new Set());
  private readonly forcedTopLevelPanels = computed<string[]>(() => {
    const filters = this.hrToolsService.filters();
    const panels: string[] = [];
    if (filters.functions?.length) panels.push('functions');
    if (filters.serviceModels?.length) panels.push('serviceModels');
    if (filters.availableCountries?.length) panels.push('countries');
    if (filters.availableLanguages?.length) panels.push('languages');
    if (filters.companySize) panels.push('companySize');
    if (filters.aiCapabilities?.length) panels.push('aiCapabilities');
    if (filters.certifications?.length) panels.push('certifications');
    return panels;
  });
  displayedOpenPanels = computed<string[]>(() => {
    const forced = this.forcedTopLevelPanels();
    const manual = this.topLevelManualOpenPanels();
    const overrideSet = this.topLevelManualOverride();
    const usedForced = forced.filter((id) => !overrideSet.has(id));
    return Array.from(new Set([...usedForced, ...manual]));
  });

  onUserToggled(newOpen: string | number | (string | number)[]) {
    const openKeys = (Array.isArray(newOpen) ? newOpen : [newOpen]).map(String);
    const previousKeys = this.displayedOpenPanels();
    const toggled = [
      ...openKeys.filter((k) => !previousKeys.includes(k)),
      ...previousKeys.filter((k) => !openKeys.includes(k)),
    ];
    this.topLevelManualOverride.update((set) => new Set([...set, ...toggled]));
    this.topLevelManualOpenPanels.set(openKeys);
  }

  private readonly nestedManualOverride = signal<Set<number>>(new Set());
  private readonly nestedManualOpenPanels = signal<number[]>([]);
  private readonly forcedNestedPanels = computed<number[]>(() => {
    const fnIds = this.hrToolsService.filters().functions || [];
    const parents = new Set<number>();
    fnIds.forEach((fnId) => {
      const m = this.findModuleByFunction(fnId);
      if (m) parents.add(m.id);
    });
    return Array.from(parents);
  });
  displayedModulePanels = computed<number[]>(() => {
    const forced = this.forcedNestedPanels();
    const manual = this.nestedManualOpenPanels();
    const overrideSet = this.nestedManualOverride();
    const usedForced = forced.filter((id) => !overrideSet.has(id));
    return Array.from(new Set([...usedForced, ...manual]));
  });

  onModuleToggled(event: string | number | (string | number)[]) {
    const openIds = (Array.isArray(event) ? event : [event]).map(Number);
    const previousIds = this.displayedModulePanels();
    const toggled = [
      ...openIds.filter((n) => !previousIds.includes(n)),
      ...previousIds.filter((n) => !openIds.includes(n)),
    ];
    this.nestedManualOverride.update((set) => new Set([...set, ...toggled]));
    this.nestedManualOpenPanels.set(openIds);
  }

  get serviceModelFormArrayControls(): FormControl<boolean>[] {
    return (this.serviceModelForm.get('selectedServiceModels') as FormArray)
      .controls as FormControl<boolean>[];
  }

  get certificationFormArrayControls(): FormControl<boolean>[] {
    return (this.certificationForm.get('selectedCertifications') as FormArray)
      .controls as FormControl<boolean>[];
  }

  constructor() {
    effect(() => {
      this.syncFunctionSelections();
      this.syncServiceModelSelections();
      this.syncCountrySelections();
      this.syncLanguageSelections();
      this.syncCertificationSelections();
      this.syncCompanySizeSelection();
    });
  }

  ngOnInit() {
    this.loadModuleFunctions();
    this.loadServiceModels();
    this.loadLanguageCodes();
    this.updateNestedPanels();

    this.subscriptions.add(
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.topLevelManualOverride.set(new Set());
        this.nestedManualOverride.set(new Set());
        this.topLevelManualOpenPanels.set([]);
        this.nestedManualOpenPanels.set([]);
        this.updateNestedPanels();
      }),
    );

    this.subscriptions.add(
      this.companySizeControl.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((val) => {
          this.onCompanySizeChange(val);
        }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private syncFunctionSelections() {
    const filters = this.hrToolsService.filters();
    this.selectedFunctionIds = new Set(filters.functions || []);
  }

  private syncServiceModelSelections() {
    const filters = this.hrToolsService.filters();
    const selectedSet = new Set(filters.serviceModels || []);
    const arrayCtrl = this.serviceModelForm.get('selectedServiceModels') as FormArray;
    if (
      this.availableServiceModels.length &&
      arrayCtrl.length !== this.availableServiceModels.length
    ) {
      arrayCtrl.clear();
      this.availableServiceModels.forEach(() =>
        arrayCtrl.push(new FormControl<boolean>(false, {nonNullable: true})),
      );
    }
    this.serviceModelFormArrayControls.forEach((ctrl, idx) => {
      ctrl.setValue(selectedSet.has(this.availableServiceModels[idx].id), {emitEvent: false});
    });
  }

  private syncCertificationSelections() {
    const filters = this.hrToolsService.filters();
    const selectedSet = new Set(filters.certifications || []);
    const arrayCtrl = this.certificationForm.get('selectedCertifications') as FormArray;
    const certifications = this.availableCertifications();
    if (certifications.length && arrayCtrl.length !== certifications.length) {
      arrayCtrl.clear();
      certifications.forEach(() =>
        arrayCtrl.push(new FormControl<boolean>(false, {nonNullable: true})),
      );
    }
    this.certificationFormArrayControls.forEach((ctrl, idx) => {
      ctrl.setValue(selectedSet.has(certifications[idx].id), {emitEvent: false});
    });
  }

  private syncCompanySizeSelection() {
    const val = this.hrToolsService.filters().companySize;
    if (this.companySizeControl.value !== val) {
      this.companySizeControl.setValue(val ?? null, {emitEvent: false});
    }
  }

  private syncCountrySelections() {
    const filters = this.hrToolsService.filters();
    const allowed = new Set(filters.availableCountries || []);
    this.selectedCountries = this.sortedAvailableCountries().filter((c) => allowed.has(c.isoCode));
  }

  private syncLanguageSelections() {
    const filters = this.hrToolsService.filters();
    const allowed = new Set(filters.availableLanguages || []);
    this.selectedLanguages = this.sortedAvailableLanguages().filter((l) => allowed.has(l.isoCode));
  }

  private loadModuleFunctions() {
    this.subscriptions.add(
      this.directusService.loadModuleFunctionsGroupedByModule().subscribe({
        next: (map) => {
          this.moduleFunctionsMap.set(map);
          this.availableModuleList = Array.from(map.values());
          this.updateNestedPanels();
          this.referenceVersion.update((version) => version + 1);
        },
      }),
    );
  }

  private loadServiceModels() {
    this.subscriptions.add(
      this.directusService.loadHrToolServiceModels().subscribe({
        next: (models) => {
          this.availableServiceModels = models;
          this.serviceModelForm.setControl(
            'selectedServiceModels',
            new FormArray(models.map(() => new FormControl<boolean>(false, {nonNullable: true}))),
          );
          this.syncServiceModelSelections();
          this.updateNestedPanels();
          this.referenceVersion.update((version) => version + 1);
        },
      }),
    );
  }

  private loadLanguageCodes() {
    this.subscriptions.add(
      this.directusService.loadLanguageCodes().subscribe({
        next: (list) => {
          this.availableLanguages.set(list);
          const filters = this.hrToolsService.filters();
          const chosen = new Set(filters.availableLanguages || []);
          this.selectedLanguages = list.filter((l) => chosen.has(l.isoCode));
          this.updateNestedPanels();
          this.referenceVersion.update((version) => version + 1);
        },
      }),
    );
  }

  private updateNestedPanels() {
    const filters = this.hrToolsService.filters();
    this.nestedManualOverride.set(new Set());
    const modulesToOpen = new Set<number>();
    (filters.functions || []).forEach((fnId) => {
      const mod = this.findModuleByFunction(fnId);
      if (mod) modulesToOpen.add(mod.id);
    });
    this.nestedManualOpenPanels.set(Array.from(modulesToOpen));
  }

  onModuleFunctionFilterChange(id: number, checked: boolean) {
    const parentModule = this.findModuleByFunction(id)?.id;
    this.hrToolsService.filters.update((current) => {
      const set = new Set(current.functions || []);
      if (checked) set.add(id);
      else set.delete(id);
      const stillInModule = Array.from(set).some(
        (fnId) => this.findModuleByFunction(fnId)?.id === parentModule,
      );
      if (!checked && !stillInModule && parentModule) {
        this.nestedManualOpenPanels.update((open) =>
          open.includes(parentModule) ? open : [...open, parentModule],
        );
      }
      return {...current, functions: Array.from(set)};
    });
    if (!checked) {
      this.topLevelManualOpenPanels.update((open) =>
        open.includes('functions') ? open : [...open, 'functions'],
      );
    }
  }

  onServiceModelFilterChange(index: number) {
    const id = this.availableServiceModels[index].id;
    const value = this.serviceModelFormArrayControls[index].value;
    this.hrToolsService.filters.update((current) => {
      const set = new Set(current.serviceModels || []);
      if (value) set.add(id);
      else set.delete(id);
      return {...current, serviceModels: Array.from(set)};
    });
    if ((this.hrToolsService.filters().serviceModels?.length ?? 0) === 0) {
      this.topLevelManualOpenPanels.update((open) =>
        open.includes('serviceModels') ? open : [...open, 'serviceModels'],
      );
    }
  }

  onAiCapabilityFilterChange({capability, checked}: AiCapabilitySelectionChange) {
    this.hrToolsService.filters.update((current) => {
      const set = new Set(current.aiCapabilities ?? []);
      if (checked) set.add(capability);
      else set.delete(capability);
      return {...current, aiCapabilities: Array.from(set)};
    });
    if (!checked) {
      this.topLevelManualOpenPanels.update((open) =>
        open.includes('aiCapabilities') ? open : [...open, 'aiCapabilities'],
      );
    }
  }

  onCertificationFilterChange(index: number) {
    const certDirectusUuid = this.availableCertifications()[index].id;
    const value = this.certificationFormArrayControls[index].value;
    this.hrToolsService.filters.update((current) => {
      const set = new Set(current.certifications || []);
      if (value) set.add(certDirectusUuid);
      else set.delete(certDirectusUuid);
      return {...current, certifications: Array.from(set)};
    });
    if ((this.hrToolsService.filters().certifications?.length ?? 0) === 0) {
      this.topLevelManualOpenPanels.update((open) =>
        open.includes('certifications') ? open : [...open, 'certifications'],
      );
    }
  }

  onCompanySizeChange(val: number | null) {
    this.hrToolsService.filters.update((current) => ({
      ...current,
      companySize: val ?? undefined,
    }));
    if (!val) {
      this.topLevelManualOpenPanels.update((open) =>
        open.includes('companySize') ? open : [...open, 'companySize'],
      );
    }
  }

  onCountriesSelected(selection: CountryCode[]) {
    this.hrToolsService.filters.update((current) => ({
      ...current,
      availableCountries: selection.map((c) => c.isoCode),
    }));
    if (selection.length === 0) {
      this.topLevelManualOpenPanels.update((open) =>
        open.includes('countries') ? open : [...open, 'countries'],
      );
    }
  }

  onLanguagesSelected(selection: LanguageCode[]) {
    this.hrToolsService.filters.update((current) => ({
      ...current,
      availableLanguages: selection.map((l) => l.isoCode),
    }));
    if (selection.length === 0) {
      this.topLevelManualOpenPanels.update((open) =>
        open.includes('languages') ? open : [...open, 'languages'],
      );
    }
  }

  private findModuleByFunction(fnId: number) {
    return this.availableModuleList.find((m) => m.functions.some((f) => f.id === fnId));
  }

  readonly hasActiveFilters = computed<boolean>(() => {
    const filters = this.hrToolsService.filters();
    return Boolean(
      filters.functions?.length ||
      filters.serviceModels?.length ||
      filters.availableCountries?.length ||
      filters.availableLanguages?.length ||
      filters.certifications?.length ||
      filters.aiCapabilities?.length ||
      filters.companySize ||
      this.hrToolsService.applicationTypeFilter().size,
    );
  });

  clearAllFilters(): void {
    this.hrToolsService.clearAllFilters();
  }

  /** Active selections rendered as removable chips at the top of the filter rail. */
  readonly activeChips = computed<ActiveFilterChip[]>(() => {
    this.referenceVersion(); // recompute once async reference data is available
    const lang = this.activeLanguage();
    const filters = this.hrToolsService.filters();
    const chips: ActiveFilterChip[] = [];

    this.hrToolsService.applicationTypeFilter().forEach((type) => {
      chips.push({
        kind: 'applicationType',
        id: type,
        label: this.hrToolsService.applicationTypeLabel(type),
      });
    });
    (filters.functions ?? []).forEach((functionId) => {
      const found = this.findFunctionById(functionId);
      if (found) {
        chips.push({
          kind: 'function',
          id: functionId,
          label: this.translateName(found.translations, found.name, lang),
        });
      }
    });
    (filters.serviceModels ?? []).forEach((id) => {
      const model = this.availableServiceModels.find((candidate) => candidate.id === id);
      if (model) {
        const key = model.translationKey
          ? this.translocoService.translate(model.translationKey)
          : '';
        chips.push({kind: 'serviceModel', id, label: key || model.name});
      }
    });
    (filters.aiCapabilities ?? []).forEach((capability) => {
      chips.push({
        kind: 'aiCapability',
        id: capability,
        label: this.translocoService.translate(`hrTool.aiCapability.${capability}.label`),
      });
    });
    (filters.certifications ?? []).forEach((id) => {
      const cert = this.availableCertifications().find((candidate) => candidate.id === id);
      if (cert) {
        chips.push({
          kind: 'certification',
          id,
          label: this.translateDirectusPipe.transform(cert.translations) ?? cert.id,
        });
      }
    });
    (filters.availableCountries ?? []).forEach((iso) => {
      const country = this.sortedAvailableCountries().find(
        (candidate) => candidate.isoCode === iso,
      );
      chips.push({
        kind: 'country',
        id: iso,
        label: this.translateName(country?.translations, iso, lang),
      });
    });
    (filters.availableLanguages ?? []).forEach((iso) => {
      const language = this.sortedAvailableLanguages().find(
        (candidate) => candidate.isoCode === iso,
      );
      chips.push({
        kind: 'language',
        id: iso,
        label: this.translateName(language?.translations, iso, lang),
      });
    });
    if (filters.companySize) {
      chips.push({
        kind: 'companySize',
        id: filters.companySize,
        label: String(filters.companySize),
      });
    }
    return chips;
  });

  removeChip(chip: ActiveFilterChip): void {
    switch (chip.kind) {
      case 'applicationType':
        this.hrToolsService.toggleApplicationType(chip.id as ArchitecturalPrinciple);
        break;
      case 'function':
        this.onModuleFunctionFilterChange(chip.id as number, false);
        break;
      case 'serviceModel':
        this.removeFromArrayFilter('serviceModels', chip.id as string);
        break;
      case 'aiCapability':
        this.removeAiCapabilityChip(chip.id);
        break;
      case 'certification':
        this.removeFromArrayFilter('certifications', chip.id as string);
        break;
      case 'country':
        this.removeFromArrayFilter('availableCountries', chip.id as string);
        break;
      case 'language':
        this.removeFromArrayFilter('availableLanguages', chip.id as string);
        break;
      case 'companySize':
        this.companySizeControl.setValue(null);
        break;
    }
  }

  private removeAiCapabilityChip(id: string | number): void {
    const capability = HR_TOOL_AI_CAPABILITY_TYPES.find((candidate) => candidate === id);
    if (capability) {
      this.onAiCapabilityFilterChange({capability, checked: false});
    }
  }

  private removeFromArrayFilter(
    key: 'serviceModels' | 'certifications' | 'availableCountries' | 'availableLanguages',
    id: string,
  ): void {
    this.hrToolsService.filters.update((current) => ({
      ...current,
      [key]: (current[key] ?? []).filter((value) => value !== id),
    }));
  }

  isAllInModuleSelected(module: Module): boolean {
    return (
      module.functions.length > 0 &&
      module.functions.every((f) => this.selectedFunctionIds.has(f.id))
    );
  }

  toggleAllInModule(module: Module, event: Event): void {
    event.stopPropagation();
    const shouldDeselect = this.isAllInModuleSelected(module);
    this.hrToolsService.filters.update((current) => {
      const set = new Set(current.functions ?? []);
      module.functions.forEach((f) => (shouldDeselect ? set.delete(f.id) : set.add(f.id)));
      return {...current, functions: Array.from(set)};
    });
  }

  private findFunctionById(functionId: number): ModuleFunction | undefined {
    for (const module of this.availableModuleList) {
      const found = module.functions.find((f) => f.id === functionId);
      if (found) {
        return found;
      }
    }
    return undefined;
  }

  private translateName(
    translations: DirectusTranslation[] | undefined,
    fallback: string,
    lang: string,
  ): string {
    return (
      translations?.find((t) => t.language === lang)?.name ?? translations?.[0]?.name ?? fallback
    );
  }

  readonly sortedAvailableLanguages: Signal<LanguageCode[]> = computed(() => {
    const langs = this.availableLanguages();
    const current = this.activeLanguage();
    return [...langs].sort((a, b) => {
      const nameA = a.translations?.find((t) => t.language === current)?.name ?? a.isoCode;
      const nameB = b.translations?.find((t) => t.language === current)?.name ?? b.isoCode;
      return nameA.localeCompare(nameB);
    });
  });
}
